import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { flatMap, map } from 'rxjs/operators';
import { EmpresaVO, RdaAutenticado, ResponsavelRda, UsuarioAutenticado } from '../shared/model';
import { EmpresaService } from './empresa.service';
import { ResponsavelService } from './responsavel.service';
import { SecurityService } from './security.service';
import { AnoBaseService } from './ano-base.service';

@Injectable({
  providedIn: 'root'
})
export class ResponsavelRdaService {

  constructor(
    private readonly securityService: SecurityService,
    private readonly empresaService: EmpresaService,
    private readonly anoBaseService: AnoBaseService,
    private readonly responsavelService: ResponsavelService) {
  }

  initialize(): Observable<ResponsavelRda> {
    return this.securityService
      .obterUsuarioAutenticado()
      .pipe(flatMap(usuarioAutenticado => this.carregarEmpresasUsuarioAutenticado(usuarioAutenticado)));
  }

  private carregarEmpresasUsuarioAutenticado(usuarioAutenticado: UsuarioAutenticado) {
    return this.securityService.obterRdaAutenticado().pipe(
      flatMap(rdaAutenticado => {
        return this.carregarEmpresasRda(usuarioAutenticado, rdaAutenticado);
      })
    );
  }

  private carregarEmpresasRda(usuarioAutenticado: UsuarioAutenticado, rdaAutenticado: RdaAutenticado): Observable<ResponsavelRda> {
    return this.empresaService.obterEmpresasRdaAutenticado().pipe(
      flatMap(empresas => this.carregarEmpresaResponsavel(rdaAutenticado, empresas))
    );
  }

  private carregarEmpresaResponsavel(rdaAutenticado: RdaAutenticado,
    page: EmpresaVO[]): Observable<ResponsavelRda> {
    const empresaResponsavel = page.find(o => o.responsavelPreenchimento);

    if (empresaResponsavel) {
      return this.carregarResponsaveis(rdaAutenticado, empresaResponsavel, page);
    }

    const responsavelRda: ResponsavelRda = {
      empresas: page,
      anoBase: this.anoBaseService.obterAnoBaseAutenticado().numero,
      rda: rdaAutenticado,
      empresaResponsavel: null,
      responsaveis: []
    };

    return of(responsavelRda);
  }

  private carregarResponsaveis(rdaAutenticado: RdaAutenticado,
    empresa: EmpresaVO,
    page: EmpresaVO[]): Observable<ResponsavelRda> {
    return this.responsavelService.obterPorComposicao(empresa.idComposicaoRda).pipe(
      flatMap(value => {
        const responsavelRda: ResponsavelRda = {
          empresas: page,
          anoBase: this.anoBaseService.obterAnoBaseAutenticado().numero,
          rda: rdaAutenticado,
          empresaResponsavel: empresa,
          responsaveis: value
        };

        return of(responsavelRda);
      })
    );
  }

  isResponsavelIndicado(): Observable<boolean> {
    return this.securityService.obterRdaAutenticado().pipe(
      map(rdaAutenticado => {
        const responsavelPreenchimento = rdaAutenticado.composicaoRdaResponsavelPreenchimento;
        const quantidadeEmpresas = rdaAutenticado.quantidadeEmpresasAssociadas;
        return quantidadeEmpresas === 1 || responsavelPreenchimento !== undefined;
      })
    );
  }
}
